import { isEmpty } from "es-toolkit/compat";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { $document, docsListItemRemoteEntry, isViewerPermission } from "../document-list";

/**
 * Returns whether a document is editable.
 *
 * A document is editable if:
 * - It has local files (exists on disk), OR
 * - It has collaboration enabled (remote with collab)
 *
 * Remote-only documents without collab are read-only (view-only mode).
 */
export const $documentIsEditable = atomFamily((documentId: string | undefined) => {
	const $documentIsEditableAtom = atom(async (get) => {
		if (!documentId) {
			return false;
		}

		const hasLocal = await get($documentLocallyEditable(documentId));
		const hasCollab = await get($documentCollabEditable(documentId));

		return hasLocal || hasCollab;
	});
	$documentIsEditableAtom.debugLabel = `documentIsEditable/${documentId}`;

	return $documentIsEditableAtom;
});

// these are documents that are only shared via collab,
// and do not have any local files.
// this is important, as only the owner should have the local files right now.
export const $documentLocallyEditable = atomFamily((documentId: string | undefined) => {
	const $documentLocallyEditableAtom = atom(async (get) => {
		if (!documentId) {
			return false;
		}

		const document = get($document(documentId));
		if (!document) {
			return false;
		}

		// Reader-only documents are not locally editable even if local files exist
		if (isViewerPermission(document)) {
			return false;
		}

		return !isEmpty(document.local);
	});
	$documentLocallyEditableAtom.debugLabel = `documentLocallyEditable/${documentId}`;

	return $documentLocallyEditableAtom;
});

export const $documentCollabEditable = atomFamily((documentId: string | undefined) => {
	const $documentCollabEditableAtom = atom(async (get) => {
		if (!documentId) {
			return false;
		}

		const document = await get($document(documentId));
		if (!document) {
			return false;
		}

		const remoteEntry = docsListItemRemoteEntry(document);
		if (!remoteEntry?.collab || isEmpty(remoteEntry.collab)) {
			return false;
		}

		// Reader-only documents are not editable even with collab access
		if (isViewerPermission(remoteEntry)) {
			return false;
		}

		return true;
	});
	$documentCollabEditableAtom.debugLabel = `documentCollabEditable/${documentId}`;

	return $documentCollabEditableAtom;
});

/**
 * Returns whether a document is read-only (viewer permission).
 * This is distinct from "not editable" — a local-only doc without collab is not editable
 * but also not a viewer doc. This atom specifically identifies documents where the user
 * has been granted read-only access.
 */
export const $documentIsReadOnly = atomFamily((documentId: string | undefined) => {
	const $documentIsReadOnlyAtom = atom((get) => {
		if (!documentId) {
			return false;
		}

		const document = get($document(documentId));
		if (!document) {
			return false;
		}

		return isViewerPermission(document);
	});
	$documentIsReadOnlyAtom.debugLabel = `documentIsReadOnly/${documentId}`;

	return $documentIsReadOnlyAtom;
});
